// =====================================================================
// ADMIN · ENTREGAS v3 — órdenes de entrega derivadas de pedidos confirmados.
// Fases de inventario: Entrante → Disponible → Asignado → En tránsito → Entregado.
//  - Programar entrega = la mercancía pasa a "En tránsito" (sigue en físico).
//  - Confirmar entregada = se descuenta del inventario físico.
// =====================================================================
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type Sum = {
  order_id: string; order_number: string | null; status: string; estimated_delivery: string | null; created_at: string;
  customer_name: string | null; total_qty: number; delivered: number; in_delivery: number; pending: number;
  ready: number; incoming: number; uncovered: number; draft_deliveries: number; scheduled_deliveries: number;
};
type Delivery = {
  id: string; folio: string | null; order_id: string; status: string; created_at: string;
  scheduled_for: string | null; delivered_at: string | null; carrier: string | null; tracking: string | null;
  received_by: string | null; cancel_reason: string | null;
  order_number: string | null; customer_name: string | null; qty: number; locations: string[];
};
type Tab = 'por_programar' | 'en_transito' | 'borradores' | 'entregadas' | 'canceladas';

const ORDER_STATUS: Record<string, string> = {
  confirmed: 'Confirmado', paid: 'Pagado', shipped: 'Entrega parcial', partially_delivered: 'Entrega parcial',
  delivered: 'Entregado', in_production: 'En producción', ready_to_ship: 'Listo',
};
const fmtDate = (d: string | null) => d ? new Date(d.length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const n = (x: any) => Number(x ?? 0);

export function AdminEntregas() {
  const [tab, setTab] = useState<Tab>('por_programar');
  const [rows, setRows] = useState<Sum[]>([]);
  const [dels, setDels] = useState<Delivery[]>([]);
  const [whNames, setWhNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [planOrder, setPlanOrder] = useState<string | null>(null);
  const [deliverId, setDeliverId] = useState<Delivery | null>(null);
  const [q, setQ] = useState('');

  async function load() {
    setLoading(true);
    const [{ data: sum }, { data: dl }, { data: wh }] = await Promise.all([
      supabase.from('order_delivery_summary').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('customer_deliveries')
        .select('id, folio, order_id, status, created_at, scheduled_for, delivered_at, carrier, tracking, received_by, cancel_reason, orders(order_number, customers(business_name)), customer_delivery_items(qty, location)')
        .order('created_at', { ascending: false }).limit(500),
      supabase.from('warehouses').select('code, name'),
    ]);
    setRows(((sum as any[]) ?? []).map(r => ({ ...r, total_qty: n(r.total_qty), delivered: n(r.delivered), in_delivery: n(r.in_delivery), pending: n(r.pending), ready: n(r.ready), incoming: n(r.incoming), uncovered: n(r.uncovered) })));
    setDels(((dl as any[]) ?? []).map(d => ({
      id: d.id, folio: d.folio, order_id: d.order_id, status: d.status, created_at: d.created_at,
      scheduled_for: d.scheduled_for, delivered_at: d.delivered_at, carrier: d.carrier, tracking: d.tracking,
      received_by: d.received_by, cancel_reason: d.cancel_reason,
      order_number: d.orders?.order_number ?? null, customer_name: d.orders?.customers?.business_name ?? null,
      qty: (d.customer_delivery_items ?? []).reduce((a: number, x: any) => a + n(x.qty), 0),
      locations: Array.from(new Set((d.customer_delivery_items ?? []).map((x: any) => x.location).filter(Boolean))) as string[],
    })));
    const m: Record<string, string> = {}; for (const w of ((wh as any[]) ?? [])) m[w.code] = w.name; setWhNames(m);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const porProgramar = useMemo(() => rows.filter(r => r.pending > 0 && r.status !== 'delivered'), [rows]);
  const byStatus = (s: string) => dels.filter(d => d.status === s);
  const match = (txt: string) => !q.trim() || txt.toLowerCase().includes(q.trim().toLowerCase());

  async function cancelar(d: Delivery) {
    const reason = prompt(d.status === 'scheduled'
      ? `Cancelar ${d.folio}: la mercancía regresa a "Asignado" para este pedido. Motivo:`
      : `Cancelar borrador ${d.folio}. Motivo:`, '');
    if (reason === null) return;
    const { error } = await supabase.rpc('cancel_delivery', { p_delivery_id: d.id, p_reason: reason || null });
    if (error) { alert(error.message); return; }
    load();
  }
  async function programar(d: Delivery) {
    if (!confirm(`Programar ${d.folio}? La mercancía pasa a "En tránsito".`)) return;
    const { error } = await supabase.rpc('schedule_delivery', { p_delivery_id: d.id });
    if (error) { alert(error.message); return; }
    load();
  }

  const delList = tab === 'en_transito' ? byStatus('scheduled') : tab === 'borradores' ? byStatus('draft')
    : tab === 'entregadas' ? byStatus('delivered') : byStatus('cancelled');

  return (
    <AdminContainer title="Entregas" eyebrow="Logística · órdenes de entrega">
      <PhaseLegend />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Kpi label="Pedidos por programar" value={porProgramar.length} color="#B45309" active={tab === 'por_programar'} onClick={() => setTab('por_programar')} />
        <Kpi label="En tránsito" value={byStatus('scheduled').length} color="#1D4ED8" active={tab === 'en_transito'} onClick={() => setTab('en_transito')} />
        <Kpi label="Borradores" value={byStatus('draft').length} color="#9A7B00" active={tab === 'borradores'} onClick={() => setTab('borradores')} />
        <Kpi label="Entregadas" value={byStatus('delivered').length} color="#15803D" active={tab === 'entregadas'} onClick={() => setTab('entregadas')} />
        <Kpi label="Canceladas" value={byStatus('cancelled').length} color={theme.textMuted} active={tab === 'canceladas'} onClick={() => setTab('canceladas')} />
      </div>

      <input placeholder="Buscar pedido, folio o cliente…" value={q} onChange={e => setQ(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: `1px solid ${theme.border}`, fontSize: 14, marginBottom: 12, background: theme.bg, color: theme.text, fontFamily: theme.font.sans }} />

      {loading ? <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div> : tab === 'por_programar' ? (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 980 }}>
            <thead><tr style={{ background: theme.bgSoft }}>
              <Th>Pedido</Th><Th>Cliente</Th><Th>Estatus</Th><Th>Entrega estim.</Th>
              <Th style={{ textAlign: 'right' }}>Entregado</Th><Th style={{ textAlign: 'right' }}>Por programar</Th><Th>Fases</Th><Th> </Th>
            </tr></thead>
            <tbody>
              {porProgramar.filter(o => match(`${o.order_number} ${o.customer_name}`)).map(o => (
                <tr key={o.order_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <Td><Mono>{o.order_number ?? o.order_id.slice(0, 8)}</Mono></Td>
                  <Td><b style={{ color: theme.brand }}>{o.customer_name ?? '—'}</b></Td>
                  <Td><Tag>{ORDER_STATUS[o.status] ?? o.status}</Tag></Td>
                  <Td style={{ color: theme.textMuted }}>{fmtDate(o.estimated_delivery)}</Td>
                  <Td style={{ textAlign: 'right', color: theme.textMuted }}>{o.delivered} / {o.total_qty}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700, color: '#B45309' }}>{o.pending}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {o.ready > 0 && <Chip {...PH.asignado}>Asignado {o.ready}</Chip>}
                      {o.in_delivery > 0 && <Chip {...PH.transito}>En tránsito {o.in_delivery}</Chip>}
                      {o.incoming > 0 && <Chip {...PH.entrante}>Entrante {o.incoming}</Chip>}
                      {o.uncovered > 0 && <Chip bg="#FEE2E2" fg="#B91C1C">Sin cubrir {o.uncovered}</Chip>}
                      {o.draft_deliveries > 0 && <Chip bg="#FEF9C3" fg="#854D0E">{o.draft_deliveries} borrador</Chip>}
                    </div>
                  </Td>
                  <Td><button onClick={() => setPlanOrder(o.order_id)} style={btnPrimary}>📮 Programar entrega</button></Td>
                </tr>
              ))}
            </tbody>
          </table>
          {porProgramar.length === 0 && <Empty>Sin pedidos pendientes de entrega.</Empty>}
        </div>
      ) : (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 980 }}>
            <thead><tr style={{ background: theme.bgSoft }}>
              <Th>Folio</Th><Th>Pedido</Th><Th>Cliente</Th>
              <Th>{tab === 'entregadas' ? 'Entregada' : 'Programada para'}</Th>
              <Th style={{ textAlign: 'right' }}>Piezas</Th><Th>Sale de</Th>
              <Th>{tab === 'entregadas' ? 'Recibió' : tab === 'canceladas' ? 'Motivo' : 'Transporte'}</Th><Th> </Th>
            </tr></thead>
            <tbody>
              {delList.filter(d => match(`${d.folio} ${d.order_number} ${d.customer_name}`)).map(d => (
                <tr key={d.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <Td><Mono>{d.folio ?? '—'}</Mono></Td>
                  <Td><Mono>{d.order_number ?? '—'}</Mono></Td>
                  <Td><b style={{ color: theme.brand }}>{d.customer_name ?? '—'}</b></Td>
                  <Td style={{ color: theme.textMuted }}>{tab === 'entregadas' ? fmtDate(d.delivered_at) : fmtDate(d.scheduled_for)}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700 }}>{d.qty}</Td>
                  <Td style={{ fontSize: 12 }}>{d.locations.map(l => whNames[l] ?? l).join(', ') || '—'}</Td>
                  <Td style={{ fontSize: 12, color: theme.textMuted }}>
                    {tab === 'entregadas' ? (d.received_by ?? '—') : tab === 'canceladas' ? (d.cancel_reason ?? '—') : [d.carrier, d.tracking].filter(Boolean).join(' · ') || '—'}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <a href={`/entrega/${d.id}/recibo`} target="_blank" rel="noreferrer" style={btnGhost}>🖨 Remisión</a>
                      {d.status === 'draft' && <button onClick={() => programar(d)} style={btnPrimary}>Programar</button>}
                      {d.status === 'scheduled' && <button onClick={() => setDeliverId(d)} style={btnPrimary}>✓ Confirmar entregada</button>}
                      {(d.status === 'draft' || d.status === 'scheduled') && <button onClick={() => cancelar(d)} style={{ ...btnGhost, color: '#B91C1C' }}>Cancelar</button>}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {delList.length === 0 && <Empty>Sin órdenes de entrega en este estatus.</Empty>}
        </div>
      )}

      {planOrder && <PlanModal orderId={planOrder} onClose={() => setPlanOrder(null)} onSaved={() => { setPlanOrder(null); setTab('en_transito'); load(); }} />}
      {deliverId && <DeliveredModal d={deliverId} onClose={() => setDeliverId(null)} onSaved={() => { setDeliverId(null); setTab('entregadas'); load(); }} />}
    </AdminContainer>
  );
}

// ---------------------------------------------------------------------
type PlanWh = { code: string; name: string; assigned: number; available: number; deliverable: number };
type PlanLine = {
  item_id: string; sku: string | null; description: string | null; unit: string | null;
  quantity: number; delivered: number; in_delivery: number; in_draft: number; pending: number; incoming: number;
  recipe: boolean; warehouses: PlanWh[];
};

function PlanModal({ orderId, onClose, onSaved }: { orderId: string; onClose: () => void; onSaved: () => void }) {
  const [order, setOrder] = useState<any>(null);
  const [lines, setLines] = useState<PlanLine[]>([]);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [loc, setLoc] = useState<Record<string, string>>({});
  const [head, setHead] = useState({ scheduled_for: new Date().toISOString().slice(0, 10), carrier: '', tracking: '', address: '', contact: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [addrs, setAddrs] = useState<{ id: string; label: string | null; kind: string; text: string | null; contact_name: string | null; contact_phone: string | null; delivery_notes: string | null }[]>([]);
  const [addrId, setAddrId] = useState<string>('');

  useEffect(() => {
    (async () => {
      const { data: ao } = await supabase.rpc('delivery_address_options', { p_order_id: orderId });
      const list = (((ao as any)?.addresses ?? []) as any[]);
      setAddrs(list);
      setAddrId(((ao as any)?.order_shipping_address_id as string) ?? (list[0]?.id ?? ''));
      const { data, error } = await supabase.rpc('delivery_planner', { p_order_id: orderId });
      if (error) { setErr(error.message); setLoading(false); return; }
      const ls = (((data as any)?.lines ?? []) as any[]).map(l => ({
        ...l, quantity: n(l.quantity), delivered: n(l.delivered), in_delivery: n(l.in_delivery), in_draft: n(l.in_draft),
        pending: n(l.pending), incoming: n(l.incoming),
        warehouses: (l.warehouses ?? []).map((w: any) => ({ ...w, assigned: n(w.assigned), available: n(w.available), deliverable: n(w.deliverable) })),
      })) as PlanLine[];
      setOrder((data as any)?.order ?? null);
      setLines(ls);
      // Bodega sugerida: la que tenga más asignado; si no, la que más pueda surtir
      const l0: Record<string, string> = {}; const q0: Record<string, string> = {};
      for (const l of ls) {
        const opts = l.warehouses.filter(w => w.deliverable > 0).sort((a, b) => (b.assigned - a.assigned) || (b.deliverable - a.deliverable));
        if (opts[0]) {
          l0[l.item_id] = opts[0].code;
          const can = Math.min(l.pending, opts[0].deliverable);
          q0[l.item_id] = can > 0 ? String(Math.floor(can)) : '';
        }
      }
      setLoc(l0); setQty(q0);
      setLoading(false);
    })();
  }, [orderId]);

  const maxFor = (l: PlanLine) => {
    const w = l.warehouses.find(x => x.code === loc[l.item_id]);
    return Math.floor(Math.min(l.pending, w?.deliverable ?? 0));
  };
  const invalid = lines.some(l => n(qty[l.item_id]) > maxFor(l) || (n(qty[l.item_id]) > 0 && n(qty[l.item_id]) !== Math.floor(n(qty[l.item_id]))));
  const totalPzs = lines.reduce((a, l) => a + n(qty[l.item_id]), 0);

  async function save(schedule: boolean) {
    setErr(null);
    const payload = lines.filter(l => n(qty[l.item_id]) > 0).map(l => ({ item_id: l.item_id, qty: n(qty[l.item_id]), location: loc[l.item_id] }));
    if (!payload.length) { setErr('Captura al menos una cantidad a entregar.'); return; }
    setBusy(true);
    const { error } = await supabase.rpc('create_delivery_order', {
      p_order_id: orderId, p_lines: payload, p_scheduled_for: head.scheduled_for || null,
      p_carrier: head.carrier || null, p_tracking: head.tracking || null, p_address: head.address || null,
      p_contact: head.contact || null, p_note: head.note || null, p_schedule: schedule,
      p_shipping_address_id: addrId || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }

  return (
    <Modal onClose={onClose} wide>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>Nueva orden de entrega</div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '4px 0 14px' }}>
        {order?.order_number ?? '…'} · {order?.customer_name ?? ''}
      </h2>
      {loading ? <div style={{ padding: 20, color: theme.textMuted }}>Calculando disponibilidad…</div> : (
        <>
          <div style={{ border: `1px solid ${theme.border}`, overflowX: 'auto', marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
              <thead><tr style={{ background: theme.bgSoft }}>
                <Th>Producto</Th><Th style={{ textAlign: 'right' }}>Pedido</Th><Th style={{ textAlign: 'right' }}>Entregado</Th>
                <Th style={{ textAlign: 'right' }}>En tránsito</Th><Th style={{ textAlign: 'right' }}>Pendiente</Th>
                <Th>¿Dónde está?</Th><Th>Sale de</Th><Th style={{ textAlign: 'right' }}>Entregar</Th>
              </tr></thead>
              <tbody>
                {lines.map(l => {
                  const withStock = l.warehouses.filter(w => w.deliverable > 0);
                  const max = maxFor(l);
                  const over = n(qty[l.item_id]) > max;
                  return (
                    <tr key={l.item_id} style={{ borderTop: `1px solid ${theme.border}`, opacity: l.pending > 0 ? 1 : 0.5 }}>
                      <Td><Mono>{l.sku}</Mono><div style={{ fontSize: 11, color: theme.textMuted }}>{l.description}{l.recipe ? ' · configurada (cuerpo + óptica)' : ''}</div></Td>
                      <Td style={{ textAlign: 'right' }}>{l.quantity}</Td>
                      <Td style={{ textAlign: 'right', color: '#15803D' }}>{l.delivered || '—'}</Td>
                      <Td style={{ textAlign: 'right', color: '#1D4ED8' }}>{l.in_delivery || '—'}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700 }}>{l.pending}</Td>
                      <Td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {l.warehouses.filter(w => w.deliverable > 0 || w.assigned > 0).map(w => (
                            <span key={w.code} style={{ fontSize: 11 }}>
                              <b>{w.name}</b>: {w.assigned > 0 && <span style={{ color: PH.asignado.fg }}>{w.assigned} asignado</span>}
                              {w.assigned > 0 && w.available > 0 && ' + '}
                              {w.available > 0 && <span style={{ color: PH.disponible.fg }}>{w.available} disponible</span>}
                            </span>
                          ))}
                          {withStock.length === 0 && l.pending > 0 && (
                            <span style={{ fontSize: 11, color: '#B91C1C' }}>Sin existencia{l.incoming > 0 ? ` · entrante ${l.incoming}` : ''}</span>
                          )}
                          {l.in_draft > 0 && <span style={{ fontSize: 10, color: '#854D0E' }}>{l.in_draft} en borrador</span>}
                        </div>
                      </Td>
                      <Td>
                        {l.pending <= 0 ? '—' : withStock.length > 1 ? (
                          <select value={loc[l.item_id] ?? ''} onChange={e => setLoc(s => ({ ...s, [l.item_id]: e.target.value }))}
                            style={{ padding: '5px 8px', border: `1px solid ${theme.accent}`, background: theme.bg, color: theme.text, fontSize: 12, fontFamily: theme.font.sans }}>
                            {withStock.map(w => <option key={w.code} value={w.code}>{w.name} (hasta {Math.floor(w.deliverable)})</option>)}
                          </select>
                        ) : withStock.length === 1 ? (
                          <span style={{ fontSize: 11, background: theme.bgSoft, padding: '3px 7px', color: theme.brand }}>ⓘ Solo en {withStock[0].name}</span>
                        ) : <span style={{ fontSize: 11, color: theme.textMuted }}>—</span>}
                      </Td>
                      <Td style={{ textAlign: 'right' }}>
                        <input type="number" min={0} step={1} max={max} disabled={l.pending <= 0 || withStock.length === 0}
                          value={qty[l.item_id] ?? ''} onChange={e => setQty(s => ({ ...s, [l.item_id]: e.target.value }))}
                          style={{ width: 70, padding: '4px 6px', textAlign: 'right', border: `1px solid ${over ? '#B91C1C' : theme.border}`, background: theme.bg, color: theme.text, fontSize: 12 }} />
                        {over && <div style={{ fontSize: 10, color: '#B91C1C' }}>máx {max}</div>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
            <Field label="Fecha programada"><input type="date" value={head.scheduled_for} onChange={e => setHead(h => ({ ...h, scheduled_for: e.target.value }))} style={inp} /></Field>
            <Field label="Transporte / paquetería"><input value={head.carrier} onChange={e => setHead(h => ({ ...h, carrier: e.target.value }))} placeholder="Propio, FedEx, recoge cliente…" style={inp} /></Field>
            <Field label="Guía"><input value={head.tracking} onChange={e => setHead(h => ({ ...h, tracking: e.target.value }))} style={inp} /></Field>
            <Field label="Dirección de entrega">
              <select value={addrId} onChange={e => setAddrId(e.target.value)} style={inp}>
                <option value="">— Otra / capturar a mano —</option>
                {addrs.map(a => <option key={a.id} value={a.id}>{(a.label || (a.kind === 'billing' ? 'Fiscal' : 'Entrega'))} · {a.text}</option>)}
              </select>
            </Field>
            <Field label={addrId ? 'Contacto (vacío = el de la dirección)' : 'Contacto que recibe'}><input value={head.contact} onChange={e => setHead(h => ({ ...h, contact: e.target.value }))} placeholder={addrId ? [addrs.find(a => a.id === addrId)?.contact_name, addrs.find(a => a.id === addrId)?.contact_phone].filter(Boolean).join(' · ') || 'Nombre y teléfono' : 'Nombre y teléfono'} style={inp} /></Field>
            <Field label="Nota"><input value={head.note} onChange={e => setHead(h => ({ ...h, note: e.target.value }))} style={inp} /></Field>
          </div>
          {!addrId && (
            <div style={{ marginBottom: 10 }}>
              <Field label="Dirección (captura libre)"><input value={head.address} onChange={e => setHead(h => ({ ...h, address: e.target.value }))} placeholder="Calle, número, colonia, CP, ciudad" style={inp} /></Field>
              {addrs.length === 0 && <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>Este cliente no tiene direcciones de envío. Dalas de alta en Clientes para que salgan solas.</div>}
            </div>
          )}
          {addrId && (() => { const a = addrs.find(x => x.id === addrId); return a?.delivery_notes ? <div style={{ fontSize: 12, color: theme.brand, background: theme.bgSoft, padding: '6px 10px', marginBottom: 10 }}>📝 Notas de entrega: {a.delivery_notes}</div> : null; })()}
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12 }}>
            Al <b>programar</b>, la mercancía pasa a <b>En tránsito</b> (sigue en el inventario físico). Se descuenta del físico hasta que confirmes la entrega.
            Si un producto está en dos bodegas y quieres sacar de ambas, haz dos órdenes de entrega.
          </div>
        </>
      )}
      {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10, padding: 10, border: '1px solid #B91C1C', background: 'rgba(220,38,38,0.05)' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: theme.textMuted, marginRight: 'auto' }}>{totalPzs} pza(s) en esta entrega</span>
        <button onClick={onClose} style={btnGhost}>Cancelar</button>
        <button onClick={() => save(false)} disabled={busy || invalid} style={{ ...btnGhost, opacity: busy || invalid ? 0.5 : 1 }}>Guardar borrador</button>
        <button onClick={() => save(true)} disabled={busy || invalid} style={{ ...btnPrimary, opacity: busy || invalid ? 0.5 : 1 }}>{busy ? 'Guardando…' : '🚚 Programar entrega'}</button>
      </div>
    </Modal>
  );
}

function DeliveredModal({ d, onClose, onSaved }: { d: Delivery; onClose: () => void; onSaved: () => void }) {
  const [who, setWho] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!who.trim()) { setErr('Captura quién recibió.'); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('mark_delivery_delivered', { p_delivery_id: d.id, p_received_by: who.trim(), p_evidence_url: evidence.trim() || null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }
  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>Confirmar entrega</div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '4px 0 6px' }}>{d.folio} · {d.customer_name}</h2>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>{d.qty} pza(s) · se descontarán del inventario físico y el pedido se marca como entregado (total o parcial).</div>
      <Field label="Recibió (nombre)"><input value={who} onChange={e => setWho(e.target.value)} style={inp} autoFocus /></Field>
      <div style={{ height: 10 }} />
      <Field label="Evidencia (liga a foto / remisión firmada, opcional)"><input value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="https://…" style={inp} /></Field>
      {err && <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={btnGhost}>Cancelar</button>
        <button onClick={go} disabled={busy} style={btnPrimary}>{busy ? 'Confirmando…' : '✓ Confirmar entregada'}</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------
const PH = {
  entrante: { bg: '#F3E8FF', fg: '#6B21A8' },
  disponible: { bg: '#DCFCE7', fg: '#15803D' },
  asignado: { bg: '#E0E7FF', fg: '#3730A3' },
  transito: { bg: '#DBEAFE', fg: '#1D4ED8' },
  entregado: { bg: '#F1F5F9', fg: '#334155' },
};
function PhaseLegend() {
  const items: [keyof typeof PH, string][] = [['entrante', 'Entrante'], ['disponible', 'Disponible'], ['asignado', 'Asignado'], ['transito', 'En tránsito'], ['entregado', 'Entregado']];
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14, fontSize: 11, color: theme.textMuted }}>
      {items.map(([k, label], i) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Chip {...PH[k]}>{label}</Chip>{i < items.length - 1 && '→'}
        </span>
      ))}
    </div>
  );
}
function Modal({ children, onClose, wide }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 }}>
      <div style={{ position: 'relative', background: theme.bg, width: '100%', maxWidth: wide ? 1100 : 520, padding: 28, border: `1px solid ${theme.border}` }}>
        <button onClick={onClose} aria-label="Cerrar" style={{ position: 'absolute', top: 12, right: 14, background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: theme.textMuted }}>×</button>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>{label}</div>{children}</label>;
}
function Kpi({ label, value, color, active, onClick }: { label: string; value: number; color: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ textAlign: 'left', minWidth: 160, padding: '12px 16px', cursor: 'pointer', background: theme.bg, border: `1px solid ${active ? color : theme.border}`, borderTop: `3px solid ${color}`, fontFamily: theme.font.sans }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: theme.font.head, fontSize: 24, fontWeight: 600, color }}>{value}</div>
    </button>
  );
}
function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, ...style }}>{children}</th>;
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '9px 10px', verticalAlign: 'top', ...style }}>{children}</td>;
}
function Tag({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 7px', background: theme.bgSoft, color: theme.brand }}>{children}</span>;
}
function Chip({ children, bg, fg }: { children: React.ReactNode; bg: string; fg: string }) {
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', background: bg, color: fg, whiteSpace: 'nowrap' }}>{children}</span>;
}
function Mono({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{children}</span>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>{children}</div>;
}
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans };
const btnPrimary: React.CSSProperties = { background: theme.accent, color: '#0e0e0e', border: 'none', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans, textDecoration: 'none', whiteSpace: 'nowrap' };
const btnGhost: React.CSSProperties = { background: 'transparent', color: theme.brand, border: `1px solid ${theme.border}`, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, textDecoration: 'none', whiteSpace: 'nowrap' };
